import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import StatCard from "../../components/cockpit/StatCard";
import { fetchCockpitStats, type CockpitStats } from "../../lib/admin";
import { formatMontant } from "@/lib/formatMontant";

function fmt(n: number): string {
  return formatMontant(n);
}

function PaysBar({ label, value, max }: { label: string; value: number; max: number }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between text-sm">
        <span className="text-slate-600">{label}</span>
        <span className="tabular-nums text-slate-500">{fmt(value)}</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full bg-gradient-to-r from-orange-600 to-orange-400 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default function CockpitDashboard() {
  const [stats, setStats] = useState<CockpitStats | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetchCockpitStats()
      .then((s) => {
        if (alive) setStats(s);
      })
      .catch(() => {
        if (alive) setErr("Impossible de charger les statistiques.");
      });
    return () => {
      alive = false;
    };
  }, []);

  if (err) {
    return <p className="text-sm text-red-400">{err}</p>;
  }
  if (!stats) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const maxPays = Math.max(stats.dossiers_ci, stats.dossiers_sn, stats.dossiers_cm, 1);

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <div>
        <h2 className="text-xl font-bold text-slate-900">Vue d’ensemble</h2>
        <p className="mt-1 text-sm text-slate-500">Données consolidées (équivalent SQL admin_vue_stats).</p>
      </div>

      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-[#e4e8ef] bg-[#e4e8ef] sm:grid-cols-4">
        <StatCard inGrid accent title="Utilisateurs" value={fmt(stats.total_users)} />
        <StatCard inGrid title="Nouveaux (semaine)" value={fmt(stats.nouveaux_semaine)} hint="Créations compte" />
        <StatCard inGrid title="Actifs 30 jours" value={fmt(stats.actifs_30j)} hint="Dernière connexion" />
        <StatCard inGrid title="Dossiers" value={fmt(stats.total_dossiers)} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-slate-700/80 bg-slate-800/50 p-6">
          <h3 className="text-sm font-semibold text-white">Répartition dossiers par pays</h3>
          <p className="mt-1 text-xs text-slate-500">Volume par code pays (CI, SN, CM)</p>
          <div className="mt-6 space-y-5">
            <PaysBar label="Côte d’Ivoire (CI)" value={stats.dossiers_ci} max={maxPays} />
            <PaysBar label="Sénégal (SN)" value={stats.dossiers_sn} max={maxPays} />
            <PaysBar label="Cameroun (CM)" value={stats.dossiers_cm} max={maxPays} />
          </div>
        </div>

        <div className="rounded-lg border border-[#e4e8ef] bg-white p-6">
          <h3 className="text-[13px] font-semibold text-slate-900">Activité comptable & facturation</h3>
          <p className="mt-1 text-[11px] text-slate-400">Écritures moteur + factures vente et achat</p>
          <div className="mt-8 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-[#e4e8ef] bg-[#e4e8ef]">
            <StatCard inGrid accent title="Écritures" value={fmt(stats.nb_ecritures)} />
            <StatCard inGrid title="Factures (tous types)" value={fmt(stats.nb_factures)} />
          </div>
        </div>
      </div>
    </div>
  );
}
